
import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoaderCircle, X } from "lucide-react";
import { toast } from "sonner";
import { isBrowser } from "@/utils/runtime";

export interface LeadFormData {
  name: string;
  email: string;
  phone: string;
}

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: LeadFormData) => void;
  isSubmitting?: boolean;
  selectedPlan?: string;
  title?: string;
  description?: string;
  source?: string;
}

const formSchema = z.object({
  name: z.string().min(3, "Nome precisa ter pelo menos 3 caracteres"),
  email: z.string().email("E-mail inválido"),
  phone: z.string().min(10, "Telefone precisa incluir DDD e número")
});

const LeadCaptureModal: React.FC<LeadCaptureModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  selectedPlan,
  title = "Solicitar orçamento",
  description = "Preencha seus dados para receber seu orçamento personalizado",
  source = "Não informado"
}) => {
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  
  const form = useForm<LeadFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: ""
    }
  });

  const handleSubmit = async (data: LeadFormData) => {
    if (!isBrowser) return;
    setIsFormSubmitting(true);
    console.log('Iniciando envio do formulário:', { data, source, selectedPlan });
    
    try {
      // Criar um formulário HTML oculto para envio
      const form = globalThis.document.createElement('form');
      form.method = 'POST';
      form.action = 'https://formsubmit.co/toctoctecnologia@gmail.com';
      form.style.display = 'none';

      // Adicionar campos como inputs ocultos
      const fields = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        _captcha: 'false',
        _subject: 'Novo orçamento solicitado via site',
        _next: globalThis.location.origin + '/contato?success=true',
        source: source,
        ...(selectedPlan && { plano: selectedPlan })
      };

      Object.entries(fields).forEach(([key, value]) => {
        const input = globalThis.document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });

      // Adicionar ao DOM e submeter
      globalThis.document.body.appendChild(form);
      form.submit();

      // Limpar o formulário do DOM
      globalThis.document.body.removeChild(form);
      
      // Mostrar feedback de sucesso
      toast.success("Dados enviados com sucesso!", {
        description: "Entraremos em contato em breve."
      });
      
      // Chamar callback e fechar modal
      onSubmit(data);
      form.reset();
      onClose();
      
    } catch (error) {
      console.error('Erro ao enviar lead:', error);
      toast.error("Erro ao enviar dados", {
        description: "Por favor, tente novamente mais tarde."
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white border-0 shadow-2xl rounded-2xl p-0 max-h-[90vh] overflow-y-auto">
        {/* Header with close button */}
        <div className="flex justify-between items-center p-6 pb-0">
          <div></div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="px-8 pb-8">
          <DialogHeader className="text-center space-y-3 mb-6">
            <DialogTitle className="text-3xl font-bold text-[#323031]">{title}</DialogTitle>
            <DialogDescription className="text-base text-[#8B8680]">{description}</DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <FormField 
                control={form.control} 
                name="name" 
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#323031] font-medium text-base">Nome</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Seu nome completo" 
                        className="border-2 border-gray-200 focus:border-[#FF5605] rounded-lg py-4 px-4 text-[#323031] placeholder:text-[#8B8680] text-base"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )} 
              />

              <FormField 
                control={form.control} 
                name="email" 
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#323031] font-medium text-base">E-mail</FormLabel>
                    <FormControl>
                      <Input 
                        type="email" 
                        placeholder="seu@email.com" 
                        className="border-2 border-gray-200 focus:border-[#FF5605] rounded-lg py-4 px-4 text-[#323031] placeholder:text-[#8B8680] text-base"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )} 
              />

              <FormField 
                control={form.control} 
                name="phone" 
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[#323031] font-medium text-base">Telefone</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="(00) 00000-0000" 
                        className="border-2 border-gray-200 focus:border-[#FF5605] rounded-lg py-4 px-4 text-[#323031] placeholder:text-[#8B8680] text-base"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )} 
              />

              <Button 
                type="submit" 
                className="w-full bg-[#FF5605] hover:bg-[#e44e05] text-white font-semibold py-4 rounded-lg text-lg transition-all duration-200 shadow-lg hover:shadow-xl mt-8" 
                disabled={isFormSubmitting || isSubmitting}
              >
                {isFormSubmitting || isSubmitting ? (
                  <>
                    <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Enviar e Continuar"
                )}
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureModal;
